import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { SlbNotificationModule } from '@slb-dls/angular-material/notification';
import { SLB_MOMENT_DATE_FORMATS } from '@slb-dls/angular-material/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfigurationService } from '../../core/services/app-configuration.service';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { AbstractSecurityStorage, AuthModule, EventTypes, OidcSecurityService, PublicEventsService, StsConfigHttpLoader, StsConfigLoader, } from '@delfi-gui/angular-auth-oidc-client';
import { StoreModule } from '@ngrx/store';
import { fromRoot } from '../../core/utilities/ngrx-loader.utility';
import { DevPortalAuthConfig } from '../../configs/dev-portal-auth-config';
import { DelfiGuiAuthModule, DelfiUserProfileModule } from '@delfi-gui/components';
import { SlbNotificationsPanelModule } from '@slb-dls/angular-material/notifications-panel';
import { TokenInterceptor } from '../../core/interceptors/token.interceptor';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MasterModule } from '../master/master.module';
import { DpAuthModule } from '../authorized/auth.module';
import { GlobalErrorHandler } from '../../core/handlers/global-error.handler';
import { SentimentSurveyModule, SLB_SENTIMENT_CONFIG } from '@slb-ux/sentiment-survey';
import { SlbSharedModule } from '@slb-dls/angular-material/shared';
import { SlbButtonModule } from '@slb-dls/angular-material/button';
import { SlbFormFieldModule } from '@slb-dls/angular-material/form-field';
import { SlbPopoverModule } from '@slb-dls/angular-material/popover';
import { SlbNavigationFrameworkModule } from '@slb-dls/angular-material/navigation-framework';
import { SlbBreadcrumbsModule } from '@slb-dls/angular-material/breadcrumbs';
import { SlbLogoutModule } from '@slb-dls/angular-material/logout';
import { SlbErrorPageModule } from '@slb-dls/angular-material/error-page';
import { HomeComponent } from 'src/app/components/home/home.component';
import { map } from 'rxjs';
import { LoginService } from 'src/app/core/services/login.service';
import { CustomLocalStore } from 'src/app/core/stores/custom-local-storage.store';
import { ErrorPageComponent } from 'src/app/components/error-page/error-page.component';

export const STS_CONFIG_LOADER_FACTORY = (appConfigurationService: AppConfigurationService): StsConfigHttpLoader => {

  const config$ = appConfigurationService.getApplicationConfigForAuth().pipe(
    map((customConfig: any) => {
      return {
        authority: customConfig.stsServer,
        redirectUrl: customConfig.redirectUrl,
        clientId: customConfig.clientId,
        responseType: 'code',
        scope: 'openid ' + customConfig.clientId +
          ' ' + customConfig.backendClientId +
          ' ' + customConfig.userSentimentClientId +
          ' ' + customConfig.esmClientId,
        postLogoutRedirectUri: customConfig.logoutUrl,
        silentRenew: true,
        silentRenewUrl: customConfig.redirectUrl,
        renewTimeBeforeTokenExpiresInSeconds: 300,
        useRefreshToken: true,
        ignoreNonceAfterRefresh: true,
        tokenRefreshInSeconds: 300,
        startCheckSession: true,
        unauthorizedRoute: "/auth/logout",
        autoUserInfo: true,
      };
    })
  )

  return new StsConfigHttpLoader(config$);
};

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ErrorPageComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    MatProgressSpinnerModule,

    DelfiUserProfileModule,
    SlbSharedModule,
    SlbButtonModule,
    SlbFormFieldModule,
    SlbPopoverModule,
    SlbNotificationModule,
    SlbNotificationsPanelModule,
    SlbNavigationFrameworkModule,
    SlbBreadcrumbsModule,
    SlbLogoutModule,
    SlbErrorPageModule,

    MasterModule,
    DpAuthModule,
    SentimentSurveyModule,
    DelfiGuiAuthModule.forRoot(DevPortalAuthConfig),
    StoreModule.forRoot({ loader: fromRoot.loaderReducer }),
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: STS_CONFIG_LOADER_FACTORY,
        deps: [AppConfigurationService],
      },
      storage: CustomLocalStore
    }),
  ],
  providers: [
    provideAppInitializer(() => {
        const initializerFn = ((appConfigurationService: AppConfigurationService) => {
        return () => {
          return appConfigurationService.loadApplicationConfig();
        };
      })(inject(AppConfigurationService));
        return initializerFn();
      }),
    { provide: MAT_DATE_FORMATS, useValue: SLB_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    //{ provide: MessageService, useClass: MessageService },
    OidcSecurityService,
    PublicEventsService,
    { provide: AbstractSecurityStorage, useClass: CustomLocalStore },
    provideHttpClient(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: SLB_SENTIMENT_CONFIG,
      useFactory: (acs: AppConfigurationService) => {
        return { endPoint: acs.configuration.userSentimentUrl }
      },
      deps: [AppConfigurationService]
    },
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
  exports: [AuthModule],
  bootstrap: [AppComponent],
})
export class AppModule {

  constructor(private readonly eventService: PublicEventsService, private readonly loginService: LoginService) {
    this.eventService
      .registerForEvents()
      .subscribe(event => {
        switch (event.type) {
          case EventTypes.CheckSessionReceived: // When user do logout from digital home
            if (event.value === 'changed') {
              this.loginService.logoff();
            }
            break;
          case EventTypes.UserDataChanged: // Changed User Data event
            if (event.value.userData == null) {
              this.loginService.redirect(["/auth/logout"]);
            }
            break;
          case EventTypes.NewAuthenticationResult: // Change in token
            this.loginService.upsertBackendToken(event.value.isRenewProcess);
            break;
        }
      });
  }
}